import * as React from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
// import { CookieNotice } from 'gatsby-cookie-notice';
import { library, icon } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from '@reach/router';
import Logo from '../assets/logo.svg';
import Footer from './footer';
import ChevronIcon from '../assets/chevron-icon.svg';
import LayoutConsumer, { LayoutProvider } from './layout-context';
import * as icons from '../icons/icon-pack';
import Header from './header';
import { extractLanguage } from '../helpers/common';
import CookieNotice from './cookie-notice';
import useFooterInView from '../hooks/useFooterInView';

library.add({ ...icons });

// useWindowSize Hook
export function useWindowSize() {
  const [windowSize, setWindowSize] = React.useState({
    width: undefined,
    height: undefined,
  });

  React.useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize, { passive: true });
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

function Layout({ children }) {
  const location = useLocation();

  const language = extractLanguage(location.pathname);

  const [isDomReady, setIsDomReady] = React.useState(false);
  const [displayCookieNotice, setDisplayCookieNotice] = React.useState(true);
  const [cookieDefaults, setCookieDefaults] = React.useState({
    googleAnalytics: false,
    klaviyo: false,
    provenExpert: false,
    linkedIn: false,
    facebookPixel: false,
    inspectletPixel: false,
  });

  const siteData = useStaticQuery(graphql`
    query SiteMetadataQuery {
      site {
        siteMetadata {
          title
          footer {
            menuLinks {
              title
              links {
                name
                link
                icon
              }
            }
            additionalText
          }
          consent {
            description
            privacy
            imprint
            title
          }
        }
      }
      header: allStoryblokEntry(filter: {name: {eq: "Menu"}}) {
        edges {
          node {
            lang
            content: layoutContent
          }
        }
      }
      footer: allStoryblokEntry(filter: {name: {eq: "Footer"}}) {
        edges {
          node {
            lang
            content: layoutContent
          }
        }
      }
    }
  `);

  const cookieInfo = siteData.footer.edges.find(({ node }) => node.lang === language);
  const cookieData = JSON.parse(cookieInfo.node.content);
  const { siteMetadata } = siteData.site;

  // Get alternate language cookie data for the language toggle
  const altLang = language === 'en' ? 'default' : 'en';
  const altCookieInfo = siteData.footer.edges.find(({ node }) => node.lang === altLang);
  const altCookieData = altCookieInfo ? JSON.parse(altCookieInfo.node.content) : null;

  // eslint-disable-next-line max-len
  const [isNavOpen, setIsNavOpen] = React.useReducer((state) => !state, false); // initiate isNavOpen state with false
  // eslint-disable-next-line no-unused-vars
  const [isNavOpenDesktop, setIsNavOpenDesktop] = React.useState(true);

  const [isStoryblokPreview, setIsStoryblokPreview] = React.useState(false);
  React.useEffect(() => {
    const initAfterDomReady = () => {
      setIsDomReady(true);
      setDisplayCookieNotice(!document.cookie.includes('cookieConsent='));
      setCookieDefaults({
        googleAnalytics: localStorage.getItem('gatsby-gdpr-google-analytics') === 'true',
        klaviyo: localStorage.getItem('gatsby-gdpr-klaviyo') === 'true',
        provenExpert: localStorage.getItem('gatsby-gdpr-proven-expert') === 'true',
        linkedIn: localStorage.getItem('gatsby-gdpr-linked-in') === 'true',
        facebookPixel: localStorage.getItem('gatsby-gdpr-facebook-pixel') === 'true',
        inspectletPixel: localStorage.getItem('gatsby-gdpr-inspectlet-pixel') === 'true',
      });

      setIsStoryblokPreview(window.location.search.includes('_storyblok_tk[token]='));
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAfterDomReady, { once: true });
      return () => document.removeEventListener('DOMContentLoaded', initAfterDomReady);
    }

    initAfterDomReady();
    return undefined;
  }, []);

  const windowSize = useWindowSize();
  const isFooterInView = useFooterInView();

  const handleNavToggle = () => {
    if (windowSize.width >= 1473) return;
    setIsNavOpen((prev) => !prev);
    window.__setBodyScrollLock(!isNavOpen);
  };

  return (
    <LayoutProvider>
      <LayoutConsumer>
        {({ data }) => (
          <>
            <div className="dark">
              <div id="ProvenExpert_widgetbar_container" />
              <nav
                role="navigation"
                className="menu transition-width group z-50 fixed shadow-md xll:shadow-none p-4 xll:p-0 xll:pt-16 flex items-center justify-between w-full xll:h-full xll:block dark:text-white bg-gradient-to-b dark:from-[#050505] dark:to-[#171717] xll:bg-gradient-to-r from-[#FFFFFF] to-[#DDDDDD] dark:xll:from-[#050505] dark:xll:to-[#001C0A] xll:overflow-y-scroll no-scrollbar"
              >
                <Link
                  to={`${language === 'en' ? '/en' : '/'}`}
                  className="flex flex-col items-center justify-center w-fit xll:m-auto"
                  aria-label={`Go to the ${language === 'en' ? 'English' : 'main'} homepage`}
                >
                  <Logo className="h-8 transition-all logo" />
                  <span className="sr-only">
                    {siteMetadata.title}
                  </span>
                  <span className="hidden mt-1 text-sm transition-all xll:block">
                    {siteMetadata.title}
                  </span>
                </Link>
                <Link
                  to="/"
                  className="relative z-0 text-xl xll:hidden sm:text-2xl left-4"
                  aria-label={`${siteMetadata.title} - Zur Startseite`}
                >
                  {siteMetadata.title}
                </Link>
                <div className="flex">
                  <Link
                    to={`${language === 'en' ? '/en/contact' : '/contact'}`}
                    className="relative px-1 z-0 text-2xl xll:hidden right-4"
                    aria-label={language === 'en' ? 'Go to contact page' : 'Zur Kontaktseite'}
                  >
                    <FontAwesomeIcon icon={icon(icons.faMessages)} />
                  </Link>
                  <div className="menu-container transition-all h-[calc(100%-5rem)] xll:flex justify-between flex-col min-w-[32px]">
                    <div>
                      <div
                        aria-expanded={isNavOpen}
                        role="button"
                        aria-label={isNavOpen ? 'Menü schließen' : 'Menü öffnen'}
                        className="fixed z-10 flex cursor-pointer xll:hidden top-5 right-4"
                        onClick={handleNavToggle}
                        onKeyPress={handleNavToggle}
                        tabIndex="0"
                      >
                        <div
                          id="mobile-menu-toggle"
                          className={isNavOpen ? 'open' : ''}
                        >
                          <span />
                          <span />
                          <span />
                          <span />
                        </div>
                      </div>
                      <div
                        className={`fixed xll:top-16 xll:left-0 w-full xll:w-full h-full xll:h-full xll:block xll:static transition-all overflow-hidden overflow-y-scroll xll:overflow-visible xll:bg-transparent dark:xll:bg-transparent pt-12 xll:pt-0 top-0 right-0 before:content-[attr(before)] before:absolute before:w-full before:right-0 before:h-full bg-gradient-to-r from-[#FFFFFF] to-[#DDDDDD] dark:from-[#050505] dark:to-[#001C0A] no-scrollbar before:transition-all before:duration-200 xll:opacity-100 xll:pointer-events-auto before:xll:static ${
                          isNavOpen
                            ? 'opacity-100 before:top-0 before:right-0 pointer-events-auto h-full'
                            : 'opacity-0 before:!-top-full before:!-right-full pointer-events-none'
                        }`}
                      >
                        <Header
                          setIsNavOpen={setIsNavOpen}
                          headerEntries={siteData.header.edges}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
            </div>

            <a
              href="#main-content"
              className="skip-link absolute left-0 top-[-40px] bg-black text-white px-4 py-2 z-50 focus:top-0 transition-all"
            >
              {language === 'en' ? 'Skip to main content' : 'Zum Hauptinhalt springen'}
            </a>
            <a
              href="#page-footer"
              className="skip-link absolute left-0 top-[-40px] bg-black text-white px-4 py-2 z-50 focus:top-[40px] transition-all"
            >
              {language === 'en' ? 'Skip to footer' : 'Zum Seitenende springen'}
            </a>

            <main id="main-content" className="main transform-gpu pt-16 lg:pt-0 bg:white dark:bg-background dark:text-white transition-all min-h-[80vh]">
              {data.fullWidth ? (
                <div>{children}</div>
              ) : (
                <div className="container flex px-4 xll:px-8">
                  <div className="max-w-screen-lg mx-auto my-4 xll:my-16">
                    {children}
                  </div>
                </div>
              )}
            </main>
            <div className="dark">
              <Footer
                className="text-white footer transform-gpu"
                siteMetadata={siteMetadata}
                footerEntries={siteData.footer.edges}
              />
            </div>

            {!isStoryblokPreview && isDomReady && displayCookieNotice && (
            <CookieNotice
              buttonWrapperClasses="md:float-none flex flex-col md:flex-row md:gap-2 w-full md:w-auto"
              backgroundWrapperClasses="cookie-notice fixed top-0 left-0 w-full h-full z-[999999] bg-[rgba(255,255,255,0.75)] text-white md:flex md:justify-center md:items-center outline-none"
              backgroundClasses="bg-black absolute md:static bottom-4 left-4 w-[calc(100%-2rem)] p-4 md:p-8 rounded-md shadow-lg md:w-full md:max-w-[600px] max-h-[calc(100vh-2rem)] overflow-x-scroll"
              wrapperClasses="flex flex-col md:flex-row w-full items-center justify-center md:justify-evenly md:gap-2"
              personalizeButtonClasses="button-tertiary text-base px-[20px] py-[10px] w-full md:w-fit rounded p-2 mb-2 md:mb-0"
              personalizeButtonText={cookieData.show_cookies}
              acceptButtonText={cookieData.accept_all_cookie}
              acceptButtonClasses="button-primary w-full text-base text-white px-[20px] py-[10px] font-black rounded block"
              declineButtonText={cookieData.refuse_cookies}
              declineButtonClasses="button-tertiary w-full text-base px-[20px] py-[10px] mb-2 md:mb-0 rounded block"
              personalizeValidationText={cookieData.save}
              personalizeValidationClasses="button-primary"
              cookieListClasses="flex flex-col gap-4"
              cookieMapClasses="form-control text-sm md:text-base"
              cookieTitleClasses="text-lg font-bold"
              imprintText={`${language === 'en' ? 'Imprint' : siteMetadata.consent.imprint}`}
              privacyText={`${language === 'en' ? 'Privacy policy' : siteMetadata.consent.privacy}`}
              imprintClasses="text-latori-green text-xs py-[10px] font-sans font-semibold mr-4 cursor-pointer"
              privacyClasses="text-latori-green text-xs py-[10px]  font-sans font-semibold cursor-pointer"
              linkWrapperClasses="flex flex-wrap justify-center mx-auto my-4 text-center"
              altLangLabel={language === 'en' ? 'DE' : 'EN'}
              altLangTexts={altCookieData ? {
                acceptButtonText: altCookieData.accept_all_cookie,
                declineButtonText: altCookieData.refuse_cookies,
                personalizeButtonText: altCookieData.show_cookies,
                personalizeValidationText: altCookieData.save,
                imprintText: language === 'en' ? siteMetadata.consent.imprint : 'Imprint',
                privacyText: language === 'en' ? siteMetadata.consent.privacy : 'Privacy policy',
                children: (
                  <>
                    <h2 className="mb-2 text-xl font-bold">
                      {altCookieData.cookie_consent_title}
                    </h2>
                    <p className="text-sm md:text-base">
                      {altCookieData.cookie_consent_description}
                    </p>
                  </>
                ),
                cookies: [
                  { text: altCookieData.essential_cookie },
                  { title: 'Google Analytics', text: altCookieData.google_analytics_cookie },
                  { title: 'Klaviyo', text: altCookieData.klaviyo_cookie },
                  { title: 'ProvenExpert', text: altCookieData.proven_expert_cookie },
                  { title: 'LinkedIn', text: altCookieData.linkedin_cookie },
                  { title: 'Facebook Pixel', text: altCookieData.facebook_pixel_cookie },
                  { title: 'Inspectlet Pixel', text: altCookieData.inspectlet_pixel_cookie },
                ],
              } : null}
              cookies={[
                {
                  name: 'necessary',
                  editable: false,
                  default: true,
                  text: cookieData.essential_cookie,
                },
                {
                  name: 'gatsby-gdpr-google-analytics',
                  editable: true,
                  default: cookieDefaults.googleAnalytics,
                  title: 'Google Analytics',
                  text: cookieData.google_analytics_cookie,
                },
                {
                  name: 'gatsby-gdpr-klaviyo',
                  editable: true,
                  default: cookieDefaults.klaviyo,
                  title: 'Klaviyo',
                  text: cookieData.klaviyo_cookie,
                },
                {
                  name: 'gatsby-gdpr-proven-expert',
                  editable: true,
                  default: cookieDefaults.provenExpert,
                  title: 'ProvenExpert',
                  text: cookieData.proven_expert_cookie,
                },
                {
                  name: 'gatsby-gdpr-linked-in',
                  editable: true,
                  default: cookieDefaults.linkedIn,
                  title: 'LinkedIn',
                  text: cookieData.linkedin_cookie,
                },
                {
                  name: 'gatsby-gdpr-facebook-pixel',
                  editable: true,
                  default: cookieDefaults.facebookPixel,
                  title: 'Facebook Pixel',
                  text: cookieData.facebook_pixel_cookie,
                },
                {
                  name: 'gatsby-gdpr-inspectlet-pixel',
                  editable: true,
                  default: cookieDefaults.inspectletPixel,
                  title: 'Inspectlet Pixel',
                  text: cookieData.inspectlet_pixel_cookie,
                },
              ]}
            >
              <h2 className="mb-2 text-xl font-bold">
                {cookieData.cookie_consent_title}
              </h2>
              <p className="text-sm md:text-base">
                {cookieData.cookie_consent_description}
              </p>
            </CookieNotice>
            )}

            {!isFooterInView && (
              <Link
                to="/blogpost/shop-usability-award"
                className="fixed p-2 bottom-12 right-4 xll:bottom-8 xll:right-4 opacity-25 hover:opacity-100 xll:p-2 transition-opacity duration-150 ease-linear"
              >
                <picture>
                  <source
                    type="image/webp"
                    srcSet="/assets/images/logo-shop-usability-award-best-agency-128.webp"
                  />
                  <img
                    src="/assets/images/logo-shop-usability-award-best-agency-128.png"
                    alt="Shop Usability Award Winner 2023"
                    loading="lazy"
                    {...{ fetchpriority: 'low' }}
                    width="128"
                    height="90"
                    className="w-32"
                  />
                </picture>
              </Link>
            )}
          </>
        )}
      </LayoutConsumer>
    </LayoutProvider>
  );
}

export default Layout;
